// src/modules/contracts/ContractForm.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Contract,
  fetchContractById,
  createContract,
  updateContract,
} from './ContractApi';
import { CONTRACT_STATUSES } from '../../constants/contractConstants';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/globalConstants';

export type FormMode = 'create' | 'edit' | 'view';

interface Props {
  mode: FormMode;
}

const defaultContract: Contract = {
  id: '',
  bidId: '',
  contractName: '',
  referenceNo: '',
  description: '',
  status: 'DRAFT',
  paperCost: 0,
  tenderActualValue: 0,
  tenderBidValue: 0,
  tenderBidPercentage: 0,
  contractType: '',
  terminationReason: '',
  extensionCoun: 0,
  openingDate: '',
  closingDate: '',
  resultDate: '',
  draftingDate: '',
  submittedPaymentDate: '',
  awardedDate: '',
  aggreementDate: '',
  givenStartDate: '',
  givenEndDate: '',
  actualStartDate: '',
  actualEndDate: '',
  licenseId: '',
  licenseHolderName: '',
  licenseNo: '',
  comments: '',
  contractMetadata: {},
  createdAt: '',
  updatedAt: '',
  createdBy: '',
  updatedBy: '',
  isDeleted: false,
  deletedAt: '',
  deletedBy: '',
};

const ContractForm: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Centralized auth (roles + permissions)
  const { user, hasPermission } = useAuth();
  const roles: string | string[] = user?.role || [];
  const isAdmin = roles.includes('Admin');

  // Module name to check against your permissions store
  const CONTRACTS_MOD = MODULES.CONTRACTS ?? 'Contracts';

  // Permissions
  const canView = useMemo(
    () => isAdmin || hasPermission(CONTRACTS_MOD, ['view', 'report', 'edit', 'create'], 'any'),
    [hasPermission, CONTRACTS_MOD, isAdmin]
  );
  const canCreate = useMemo(
    () => isAdmin || hasPermission(CONTRACTS_MOD, ['create'], 'any'),
    [hasPermission, CONTRACTS_MOD, isAdmin]
  );
  const canEdit = useMemo(
    () => isAdmin || hasPermission(CONTRACTS_MOD, ['edit'], 'any'),
    [hasPermission, CONTRACTS_MOD, isAdmin]
  );
  // Optional: separate permission to change the status only
  const canChangeStatus = useMemo(
    () => isAdmin || hasPermission(CONTRACTS_MOD, ['changeStatus', 'edit'], 'any'),
    [hasPermission, CONTRACTS_MOD, isAdmin]
  );

  // Effective mode based on route + permissions
  const effectiveMode: FormMode = useMemo(() => {
    if (!canView) return 'view'; // show read-only notice (or redirect)
    if (mode === 'create') return canCreate ? 'create' : 'view';
    if (mode === 'edit') return canEdit ? 'edit' : 'view';
    return 'view';
  }, [mode, canView, canCreate, canEdit]);

  const [contract, setContract] = useState<Contract>(defaultContract);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isReadOnly = effectiveMode === 'view';

  useEffect(() => {
    if ((mode === 'view' || mode === 'edit') && id) {
      loadContractById(id);
    } else if (mode === 'create') {
      setContract(defaultContract);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const loadContractById = async (contractId: string) => {
    setLoading(true);
    try {
      const response = await fetchContractById(contractId);
      setContract(response.data);
    } catch (err) {
      setError('Failed to fetch contract');
    } finally {
      setLoading(false);
    }
  };

  const numberOrZero = (val: any) => {
    const n = parseFloat(String(val));
    return Number.isFinite(n) ? n : 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    if (isReadOnly) return;

    const { name, value, type } = e.target;
    let updated: Contract = { ...contract, [name]: value };

    // If editing numbers, keep invariants for bid value/percentage
    const actualValue = numberOrZero(contract.tenderActualValue);
    const inputNumber = numberOrZero(value);

    if (type === 'number' || ['tenderBidValue', 'tenderBidPercentage'].includes(name)) {
      if (name === 'tenderBidPercentage') {
        updated.tenderBidValue = actualValue + (actualValue * inputNumber) / 100;
      }
      if (name === 'tenderBidValue') {
        updated.tenderBidPercentage =
          actualValue === 0 ? 0 : ((inputNumber - actualValue) / actualValue) * 100;
      }
    }

    setContract(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (effectiveMode === 'edit' && id) {
        if (!canEdit) {
          alert('You do not have permission to update contracts.');
          return;
        }
        await updateContract(id, contract);
        alert('Contract updated successfully!');
      } else if (effectiveMode === 'create') {
        if (!canCreate) {
          alert('You do not have permission to create contracts.');
          return;
        }
        await createContract(contract);
        alert('Contract created successfully!');
        navigate(-1);
        return;
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save contract. Please try again.');
    }
  };

  const handleReset = () => {
    if (effectiveMode !== 'create') return;
    setContract(defaultContract);
  };

  const renderInput = (label: string, field: keyof Contract, type = 'text', disabled?: boolean) => (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type={type}
        name={field}
        value={(contract[field] as any) ?? ''}
        onChange={handleChange}
        readOnly={isReadOnly || disabled}
        className={`border px-3 py-2 rounded w-full ${
          isReadOnly || disabled ? 'bg-gray-100' : ''
        }`}
      />
    </div>
  );

  const renderStatusDropdown = () => (
    <div>
      <label className="block text-sm font-semibold mb-1">Status</label>
      <select
        name="status"
        value={contract.status}
        onChange={handleChange}
        disabled={isReadOnly || !canChangeStatus}
        className={`border px-3 py-2 rounded w-full ${
          isReadOnly || !canChangeStatus ? 'bg-gray-100' : ''
        }`}
        title={!canChangeStatus && !isReadOnly ? 'You lack permission to change status' : undefined}
      >
        {CONTRACT_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
      {!isReadOnly && !canChangeStatus && (
        <p className="text-xs text-amber-600 mt-1">Status change not permitted.</p>
      )}
    </div>
  );

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 mx-auto bg-white w-full shadow rounded max-w-7xl">
      {!canView && (
        <div className="mb-4 text-red-600">
          You do not have permission to view contracts.
        </div>
      )}

      {error && <div className="mb-4 text-red-600">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {renderInput('Bid ID', 'bidId')}
          {renderInput('Contract Name', 'contractName')}
          {renderInput('Reference No', 'referenceNo')}
          {renderInput('Description', 'description')}
          {renderInput('Contract Type', 'contractType')}
          {renderStatusDropdown()}
          {renderInput('Opening Date', 'openingDate', 'date')}
          {renderInput('Closing Date', 'closingDate', 'date')}
          {renderInput('Result Date', 'resultDate', 'date')}
          {renderInput('Drafting Date', 'draftingDate', 'date')}
          {renderInput('Submitted Payment Date', 'submittedPaymentDate', 'date')}
          {renderInput('Awarded Date', 'awardedDate', 'date')}
          {renderInput('Agreement Date', 'aggreementDate', 'date')}
          {renderInput('Given Start Date', 'givenStartDate', 'date')}
          {renderInput('Given End Date', 'givenEndDate', 'date')}
          {renderInput('Actual Start Date', 'actualStartDate', 'date')}
          {renderInput('Actual End Date', 'actualEndDate', 'date')}
          {renderInput('Paper Cost', 'paperCost', 'number')}
          {renderInput('Tender Actual Value', 'tenderActualValue', 'number')}
          {renderInput('Tender Bid Value', 'tenderBidValue', 'number')}
          {renderInput('Tender Bid Percentage', 'tenderBidPercentage', 'number')}
          {renderInput('Extension Count', 'extensionCoun', 'number')}
          {renderInput('Termination Reason', 'terminationReason')}
          {renderInput('License ID', 'licenseId')}
          {renderInput('License Holder Name', 'licenseHolderName')}
          {renderInput('License No', 'licenseNo')}
          {renderInput('Comments', 'comments')}
        </div>

        <div className="mt-6 flex gap-4">
          {effectiveMode !== 'view' && (
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={(effectiveMode === 'create' && !canCreate) || (effectiveMode === 'edit' && !canEdit)}
              title={
                (effectiveMode === 'create' && !canCreate)
                  ? 'You do not have permission to create contracts'
                  : (effectiveMode === 'edit' && !canEdit)
                  ? 'You do not have permission to update contracts'
                  : undefined
              }
            >
              {effectiveMode === 'create' ? 'Create' : 'Update'}
            </button>
          )}

          {effectiveMode === 'create' && (
            <button
              type="button"
              onClick={handleReset}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Reset
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContractForm;